import VueCompositionApi, {
  ref,
  Ref,
  computed,
  watch,
} from "@vue/composition-api";
import Vue from "vue";
import {
  CommitFn,
  HistoryItem,
  RegMut,
  RegAction,
  Plugin,
  RegEmptyAction,
  RegEmptyMut,
} from "./types";
import * as fp from "./fp";
import { Mutations } from "./mutations";
import { raceConditionChecker } from "./race-checker";
import { emptyFn } from "@/fp";
import VueRouter from "vue-router";
declare const chrome: any;

Vue.use(VueCompositionApi);

interface Cache<G, S extends keyof G> {
  store: (k: string, val: G[S]) => void;
  val: G[S];
  updateLast: (v: G[S]) => void;
}

export function Stato<G>(p?: {
  plugins?: Plugin<G>[];
  onError?: (e: Error, logName: string) => void;
  router?: VueRouter;
}) {
  //@ts-ignore
  window.__sababa = {
    inspect: () => appState.value,
  };

  const { plugins = [], onError } = p || {};
  let router = p?.router;
  let actionId = 1;
  const history: HistoryItem<any>[] = [];
  const appState: Ref<G> = ref({} as G);
  const readOnlyAppState = computed(() => appState.value);

  const raceChecker = raceConditionChecker();

  const addHistoryItem = (item: HistoryItem<any>) => {
    /*  history.push(item);
    try {
      plugins.forEach((plugin) => plugin(item));
    } catch (e) {
      console.log("plugin e : ", e);
    } */
  };

  const addPlugin = (plugin: Plugin<G>) => {
    plugins.push(plugin);
  };

  const attachRouter = (app_router: VueRouter) => {
    router = app_router;
  };

  const appCache: Map<
    keyof G,
    {
      store: (k: string, val: G[keyof G]) => void;
      val: G[keyof G];
      updateLast: (v: G[keyof G]) => void;
    }
  > = new Map();

  const getStateCache = <S extends keyof G>(
    stateName: S,
    viewId: string,
    seed: G[S]
  ) => {
    if (appCache.has(stateName)) {
      const cache = appCache.get(stateName);
      const lastState = appState.value[stateName];
      cache?.updateLast(lastState);
      return cache;
    } else {
      const cache = fp.cache(seed)(viewId);
      // @ts-ignore
      appCache.set(stateName, cache);
      return cache;
    }
  };

  function createState<S extends keyof G>(
    stateName: S,
    seed: G[S],
    p?: { cacheId?: string; keepLastStatesCount?: number }
  ) {
    const { cacheId, keepLastStatesCount = 0 } = p || {};

    //#region  LAST STATES
    const lastStates = ((maxCount: number) => {
      let _lastStates: Array<G[S]> = [];
      const hasPrev = () => maxCount > 0 && _lastStates.length > 0;
      const keepState = () => {
        _lastStates.push({ ...getLastState() });
        if (_lastStates.length > maxCount) {
          _lastStates.shift();
        }
      };
      const prev = (): boolean => {
        if (hasPrev()) {
          commit({
            logName: "prev state",
            payload: null,
            state: _lastStates.pop() as G[S],
          });
          return true;
        }
        return false;
      };
      const clear = () => (_lastStates = []);
      const inspect = () => _lastStates;
      return {
        keepState,
        hasPrev,
        prev,
        clear,
        inspect,
      };
    })(keepLastStatesCount);

    //@ts-ignore
    window.__sababa_lastStates = lastStates;

    //#endregion

    //#region CACHE INIT
    let cache: Cache<G, S> | null = null;
    if (cacheId) {
      // @ts-ignore
      cache = getStateCache<S>(stateName, cacheId, seed);
      seed = cache?.val as G[S];
    }
    //#endregion

    appState.value[stateName] = seed;

    const internalState: Ref<G[S]> = ref(seed);

    const readOnlyState = computed(() => internalState.value);

    const subStates: { [key: string]: Ref<unknown> } = {};

    function getLastState(): G[S] {
      return appState.value[stateName];
    }
    const timeStamp = () => Date.now();
    const sendMsgToExtension = emptyFn();

    const commit: CommitFn<G[S]> = (params: {
      logName: string;
      payload: any;
      state: G[S];
      actionId?: number;
    }) => {
      const { logName, payload, state: newState, actionId } = params;
      internalState.value = newState;
      // appState.value[stateName] = newState;
      // TODO: this causing all the computed return from getter() to run for every change
      appState.value = {
        ...appState.value,
        [stateName]: newState,
      };
      addHistoryItem({
        type: "mutation",
        logName,
        payload,
        actionId,
        stateName,
        timestamp: timeStamp(),
        state: {
          ...appState.value,
          [stateName]: {
            ...appState.value[stateName],
            subStates: {
              ...subStates,
            },
          },
        },
      });
      try {
        sendMsgToExtension();
      } catch (e) {
        console.log("error in sending history to extension");
      }
    };

    const getter = <T>(fn: (state: G[S]) => T) => {
      const f = computed(() => {
        return fn(internalState.value /* , appState.value */);
      });
      //    getters.add(f);
      return f;
    };

    const m = Mutations(getLastState, commit);
    // read set append update & delete  KEY extends keyof G[S]
    const crud = <T, KEY extends keyof G[S]>(stateKey: KEY, by: keyof T) => {
      return {
        read: getter((s) => s[stateKey]),
        set: m.set(stateKey),
        append: m.append(stateKey),
        update: m.updateOneBy(stateKey, by),
        remove: m.removeBy(stateKey, by),
      };
    };

    const mutation = <T>(
      name: string,
      fn: (state: G[S], payload: T) => G[S]
    ) => {
      return (payload: T) => {
        commit({
          logName: name,
          payload,
          state: fn(getLastState(), payload),
        });
      };
    };

    let beforeEachFn: Function | null = null;
    const beforeEachAction = (fn: (p: { mutate: RegMut }) => void) => {
      beforeEachFn = fn;
    };
    let afterEachFn: Function | null = null;
    const afterEachAction = (fn: (p: { mutate: RegMut }) => void) => {
      afterEachFn = fn;
    };

    const action = <T>(
      logName: string,
      fn: (
        params: {
          state: G[S];
          gState: G;
          mutate: RegMut;
          dispatch: RegAction;
          _dispatch: RegEmptyAction;
          _mutate: RegEmptyMut;
        },
        payload: T
      ) => Promise<void>
    ) => {
      return async (p: T) => {
        actionId++;
        addHistoryItem({
          type: "action",
          logName,
          actionId,
          stateName,
          timestamp: timeStamp(),
          payload: p,
          state: {
            ...appState.value,
            [stateName]: {
              ...appState.value[stateName],
              subStates: {
                ...subStates,
              },
            },
          },
        });

        raceChecker.addAction(logName, actionId);

        const mutate: RegMut = (() => {
          const ACTION_ID = actionId;
          return <T>(fn: (t: T) => void, t: T) => {
            fn(t);
            /*  history[history.length - 1].fromActionId = ACTION_ID;
            // checking race conditions
            const { logName, stateName } = history[history.length - 1];

            raceChecker.addMutation(ACTION_ID, stateName, logName, "");
            // logging to dev-tools
            sendMsgToExtension(); */
          };
        })();

        const _mutate: RegEmptyMut = (() => {
          const ACTION_ID = actionId;
          return <T>(fn: () => void) => {
            fn();
            /*  history[history.length - 1].fromActionId = ACTION_ID;
            // checking race conditions
            const { logName, stateName } = history[history.length - 1];

            raceChecker.addMutation(ACTION_ID, stateName, logName, "");
            // logging to dev-tools
            sendMsgToExtension(); */
          };
        })();

        const dispatch: RegAction = (() => {
          const ACTION_ID = actionId;
          return async <T>(fn: (t: T) => Promise<void>, t: T) => {
            await fn(t);
            /*  history[history.length - 1].fromActionId = ACTION_ID;
            sendMsgToExtension(); */
          };
        })();
        const _dispatch: RegEmptyAction = (() => {
          const ACTION_ID = actionId;
          return async <T>(fn: (c: void) => Promise<void>) => {
            await fn();
            /*  history[history.length - 1].fromActionId = ACTION_ID;
            sendMsgToExtension(); */
          };
        })();
        if (beforeEachFn) {
          beforeEachFn({ mutate });
        }
        try {
          await fn(
            {
              state: getLastState(),
              gState: appState.value,
              mutate,
              dispatch,
              _dispatch,
              _mutate,
            },
            p
          );
          if (afterEachFn) {
            afterEachFn({ mutate });
          }
          raceChecker.removeAction(actionId);
          return;
        } catch (e) {
          if (onError) {
            // @ts-ignore
            onError(e, logName);
            return;
          }
          throw e;
        }
      };
    };

    const trackSubState = (name: string, val: Ref<unknown>) =>
      (subStates[name] = val);

    let onDestroyFn: Function | null = null;
    const onDestroy = (fn: () => void) => {
      onDestroyFn = fn;
    };

    const attachVmLifeCycle = (vm: Vue) => {
      vm.$once("hook:destroyed", () => {
        if (cache) {
          cache.updateLast(getLastState());
        }
        if (onDestroyFn) {
          onDestroyFn();
        }
      });
    };

    const reset = mutation<void>("reset", () => {
      return {
        ...seed,
      };
    });

    const setOnReady = (condition: Ref<boolean>) => {
      let isReady = false;
      let subs: Function[] = [];
      let stopWatch: null | Function = null;
      return (cb: Function) => {
        isReady = condition.value;
        if (isReady) {
          cb();
          if (stopWatch) {
            stopWatch();
            stopWatch = null;
          }
        } else {
          stopWatch = watch(condition, (isIt: boolean) => {
            isReady = isIt;
            if (isIt) {
              subs.forEach((s) => s());
              subs = [];
            }
          });
          subs.push(cb);
        }
      };
    };

    function attachToRouter(p: {
      listenToRoutes: string[];
      onEnter: () => void;
      onLeave: () => void;
    }) {
      if (router) {
        const { listenToRoutes, onEnter, onLeave } = p;
        router.afterEach((to, from) => {
          if (to && to.name) {
            if (listenToRoutes.includes(to.name)) {
              onEnter();
              return;
            }
          }
          if (from && from.name) {
            if (listenToRoutes.includes(from.name || "")) {
              onLeave();
              return;
            }
          }
        });
      } else {
        throw Error("router is not connected to store!");
      }
    }

    return {
      attachToRouter,
      setOnReady,
      trackSubState,
      readOnlyAppState,
      readOnlyState,
      getter,
      mutation,
      action,
      fp,
      m: {
        ...m,
        crud,
      },
      beforeEachAction,
      afterEachAction,
      attachVmLifeCycle,
      onDestroy,
      reset,
      lastStates,
    };
  }

  return {
    addPlugin,
    createState,
    attachRouter,
  };
}
